import { useMemo } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeChild,
  blocksForDate,
  capturesInRange,
  dayProgress,
  ritualsForDate,
  type PlanBlock,
  type Ritual,
} from '../../store/selectors'

/**
 * The end of the day, said out loud.
 *
 * There is no score here and no completion ratio, because a day with one good
 * ten-minute thing in it is not a worse day than one with three. It names what
 * actually happened — what you did, what she said — and then gets out of the
 * way. A day where the blocks were skipped but something was captured still
 * gets a warm reading, because that day was not a failure.
 */
export function DayComplete({ date }: { date: string }) {
  const child = useAuroraStore(activeChild)
  const blocks = useAuroraStore((s: any) => blocksForDate(s, date)) as PlanBlock[]
  const progress = useAuroraStore((s: any) => dayProgress(s, date))
  const rituals = useAuroraStore((s: any) => ritualsForDate(s, date)) as Ritual[]

  const [from, to] = useMemo(() => {
    const start = new Date(`${date}T00:00:00`).getTime()
    return [start, start + 86_400_000]
  }, [date])

  const logged = useAuroraStore((s: any) => capturesInRange(s, from, to)) as any[]

  const quotes = logged.flatMap(
    (c) => (c.interpretation?.quotes ?? c.interpretation?.childQuotes ?? []) as string[],
  )
  const ritualsDone = rituals.filter((r) => r.done)
  const done = blocks.filter((b) => b.status === 'done')
  const partial = blocks.filter((b) => b.status === 'partial')

  const nothingHappened = !logged.length && !ritualsDone.length && !progress.settled
  // Wait until the day has resolved itself. Appearing early would read as a
  // nudge, and this is the one moment in the app that must never nag.
  if (nothingHappened || (blocks.length > 0 && !progress.allSettled)) return null

  const name = (child as any)?.name ?? 'they'

  const headline = quotes.length
    ? 'Worth keeping'
    : done.length && logged.length
      ? "That's the day"
      : logged.length
        ? 'Noted'
        : 'Done for today'

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  return (
    <section className="section day-done reveal" style={{ '--i': 8 } as React.CSSProperties}>
      <div className="day-done-glow" aria-hidden="true" />

      <h2 className="day-done-title">{headline}</h2>

      <ul className="day-done-list">
        {done.length > 0 && (
          <li>
            <strong>{done.map((b) => b.title).join(', ')}</strong>
            {done.length === 1 ? ': done.' : ': all done.'}
          </li>
        )}
        {partial.length > 0 && (
          <li>
            <strong>{partial.map((b) => b.title).join(', ')}</strong>
            ': partly, which counts.'
          </li>
        )}
        {progress.settled > done.length + partial.length && (
          <li>
            The rest you moved past. {name} will meet it again when it fits better.
          </li>
        )}
        {ritualsDone.length > 0 && (
          <li>
            {ritualsDone.map((r) => r.title.toLowerCase()).join(', ')}: woven in.
          </li>
        )}
        {logged.length > 0 && (
          <li>
            {logged.length === 1 ? 'One moment' : `${logged.length} moments`} captured. Sapling has
            them.
          </li>
        )}
      </ul>

      {quotes.length > 0 && (
        <figure className="day-done-quote">
          <blockquote>“{quotes[0]}”</blockquote>
          <figcaption>{name}, today</figcaption>
        </figure>
      )}

      <div className="day-done-actions">
        {logged.length === 0 ? (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => (auroraStore.getState() as any).startRecording()}
          >
            Say how it went
          </button>
        ) : (
          <button className="btn btn-quiet btn-sm" onClick={() => ask('How did today go?')}>
            What did Sapling notice?
          </button>
        )}
      </div>
    </section>
  )
}
